import React from "react";
import {
  ArrowsClockwiseIcon,
  CheckCircleIcon,
  FilePptIcon,
  FileTextIcon,
  LinkIcon,
  LockIcon,
  WarningIcon,
  XIcon,
} from "@phosphor-icons/react";
import { Platform, Pressable, ScrollView, StyleSheet, View, type ViewStyle } from "react-native";

import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { colors } from "../../../styles/design-system";
import {
  GAP_ANALYSIS_IMPORT_ISSUES_CONTENT,
  GAP_ANALYSIS_IMPORT_ISSUES_RESYNC_SUCCESS_DESCRIPTION,
  GAP_ANALYSIS_IMPORT_ISSUES_RESYNC_SUCCESS_TITLE,
  GAP_ANALYSIS_IMPORT_ISSUES_SUBTITLE,
  GAP_ANALYSIS_IMPORT_ISSUES_TITLE,
  type GapAnalysisImportIssue,
} from "./GapAnalysisImportIssuesPage.constants";

export type { GapAnalysisImportIssue } from "./GapAnalysisImportIssuesPage.constants";

interface GapAnalysisImportIssuesPageProps {
  issues?: GapAnalysisImportIssue[];
  onDismissIssue?: (issueId: string) => void;
  onClose?: () => void;
  onResync?: () => void;
  isResyncComplete?: boolean;
  disableResync?: boolean;
  width?: number;
  height?: number;
}

function GapAnalysisImportIssuesPage({
  issues,
  onDismissIssue,
  onClose,
  onResync,
  isResyncComplete = false,
  disableResync = false,
  width = 460,
  height,
}: GapAnalysisImportIssuesPageProps) {
  const resolvedIssues = issues?.length ? issues : GAP_ANALYSIS_IMPORT_ISSUES_CONTENT;
  const isResyncActionDisabled = disableResync || isResyncComplete;
  const drawerSizeStyle: ViewStyle = { width };

  if (typeof height === "number") {
    drawerSizeStyle.height = height;
  }

  return (
    <View style={[styles.drawerShell, drawerSizeStyle]} role="dialog" accessibilityLabel="Import issues">
      <View style={styles.header}>
        <CustomText
          text={GAP_ANALYSIS_IMPORT_ISSUES_TITLE}
          useTranslationText={false}
          size="l"
          weight="bold"
          style={styles.title}
        />
        <CustomText
          text={GAP_ANALYSIS_IMPORT_ISSUES_SUBTITLE}
          useTranslationText={false}
          size="s"
          weight="normal"
          style={styles.subtitle}
        />
      </View>

      {isResyncComplete ? (
        <View style={styles.successContainer}>
          <View style={styles.successIconShell}>
            <CheckCircleIcon size={26} color={colors.success[600]} weight="fill" />
          </View>
          <CustomText
            text={GAP_ANALYSIS_IMPORT_ISSUES_RESYNC_SUCCESS_TITLE}
            useTranslationText={false}
            size="s"
            weight="bold"
            style={styles.successTitle}
          />
          <CustomText
            text={GAP_ANALYSIS_IMPORT_ISSUES_RESYNC_SUCCESS_DESCRIPTION}
            useTranslationText={false}
            size="s"
            weight="normal"
            style={styles.successDescription}
          />
        </View>
      ) : (
        <ScrollView
          style={styles.issueListContainer}
          contentContainerStyle={styles.issueListContent}
          showsVerticalScrollIndicator>
          {resolvedIssues.map((issue, index) => (
            <View key={issue.id} style={[styles.issueRow, index > 0 ? styles.issueRowWithTopDivider : null]}>
              <View style={styles.issueIconBox}>{getLeadingIssueIcon(issue)}</View>
              <View style={styles.issueContent}>
                <CustomText
                  text={issue.source}
                  useTranslationText={false}
                  size="s"
                  weight="medium"
                  numberOfLines={1}
                  style={styles.issueSource}
                />
                <View style={styles.issueReasonRow}>
                  {getReasonIcon(issue)}
                  <CustomText
                    text={issue.reason}
                    useTranslationText={false}
                    size="xs"
                    weight="bold"
                    style={styles.issueReason}
                  />
                </View>
                <CustomText
                  text={issue.description}
                  useTranslationText={false}
                  size="xs"
                  weight="normal"
                  numberOfLines={2}
                  style={styles.issueDescription}
                />
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`Dismiss ${issue.source}`}
                onPress={() => onDismissIssue?.(issue.id)}
                style={(state) => [styles.dismissButton, getDismissButtonInteractionStyle(state)]}>
                <CustomText
                  text="Dismiss"
                  useTranslationText={false}
                  size="xs"
                  weight="medium"
                  style={styles.dismissButtonText}
                />
              </Pressable>
            </View>
          ))}
        </ScrollView>
      )}

      <View style={styles.footer}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close import issues"
          onPress={onClose}
          style={(state) => [styles.closeActionButton, getCloseActionButtonInteractionStyle(state)]}>
          <XIcon size={14} color={colors.grey[700]} />
          <CustomText
            text="Close"
            useTranslationText={false}
            size="xs"
            weight="medium"
            style={styles.closeActionText}
          />
        </Pressable>
        <CustomButton
          text="Re-sync"
          useTranslationText={false}
          styleType="primary"
          size="xs"
          bold={true}
          leftIcon={<ArrowsClockwiseIcon size={13} color={isResyncActionDisabled ? colors.brand[200] : colors.white} />}
          disabled={isResyncActionDisabled}
          onPress={onResync}
          accessibilityLabel="Re-sync import issues"
          style={styles.resyncButton}
          textStyle={styles.resyncText}
        />
      </View>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Close panel"
        onPress={onClose}
        style={styles.closeIconButton}>
        <XIcon size={16} color={colors.grey[700]} />
      </Pressable>
    </View>
  );
}

function getLeadingIssueIcon(issue: GapAnalysisImportIssue) {
  if (issue.id === "permission-denied") {
    return <FilePptIcon size={16} color={colors.grey[600]} />;
  }

  if (issue.id === "unsupported-format") {
    return <LinkIcon size={16} color={colors.grey[600]} />;
  }

  if (issue.source.startsWith("http") || issue.reason.toLowerCase().includes("link")) {
    return <LinkIcon size={16} color={colors.grey[600]} />;
  }

  return <FileTextIcon size={16} color={colors.grey[600]} />;
}

function getReasonIcon(issue: GapAnalysisImportIssue) {
  const lowerReason = issue.reason.toLowerCase();

  if (lowerReason.includes("permission")) {
    return <LockIcon size={12} color={colors.warning[700]} />;
  }

  if (lowerReason.includes("link")) {
    return <LinkIcon size={12} color={colors.warning[700]} />;
  }

  return <WarningIcon size={12} color={colors.warning[700]} />;
}

function getDismissButtonInteractionStyle({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) {
  return {
    backgroundColor: pressed ? colors.grey[200] : hovered ? colors.grey[100] : "transparent",
    transform: [{ scale: pressed ? 0.985 : hovered ? 1.015 : 1 }],
  };
}

function getCloseActionButtonInteractionStyle({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) {
  return {
    backgroundColor: pressed ? colors.grey[100] : hovered ? colors.grey[25] : colors.white,
    shadowOpacity: hovered ? 0.1 : 0.05,
    shadowRadius: hovered ? 8 : 4,
    transform: [{ scale: pressed ? 0.985 : hovered ? 1.015 : 1 }],
  };
}

const styles = StyleSheet.create({
  drawerShell: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 8,
    overflow: "hidden",
    flex: 1,
    minHeight: 0,
    flexDirection: "column",
  },
  header: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
    gap: 8,
    flexShrink: 0,
  },
  title: {
    color: colors.grey[900],
  },
  subtitle: {
    color: colors.grey[600],
    lineHeight: 20,
  },
  issueListContainer: {
    flex: 1,
    minHeight: 0,
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
  },
  issueListContent: {
    ...(Platform.OS === "web"
      ? ({
          overflowX: "hidden",
        } as unknown as ViewStyle)
      : {}),
  },
  issueRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  issueRowWithTopDivider: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
  },
  issueIconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: colors.grey[100],
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  issueContent: {
    flex: 1,
    minWidth: 0,
  },
  issueSource: {
    color: colors.grey[900],
    lineHeight: 20,
  },
  issueReasonRow: {
    marginTop: 4,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  issueReason: {
    color: colors.warning[700],
    lineHeight: 16,
  },
  issueDescription: {
    marginTop: 4,
    color: colors.grey[600],
    lineHeight: 17,
  },
  dismissButton: {
    alignItems: "center",
    justifyContent: "center",
    minHeight: 28,
    borderRadius: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    flexShrink: 0,
    ...(Platform.OS === "web"
      ? ({
          cursor: "pointer",
          transitionProperty: "transform, background-color",
          transitionDuration: "120ms",
          transitionTimingFunction: "ease-out",
        } as unknown as ViewStyle)
      : {}),
  },
  dismissButtonText: {
    color: colors.grey[700],
  },
  footer: {
    minHeight: 50,
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
    paddingHorizontal: 24,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexShrink: 0,
  },
  closeActionButton: {
    minHeight: 30,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 4,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: colors.white,
    justifyContent: "center",
    paddingHorizontal: 10,
    paddingVertical: 4,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    ...(Platform.OS === "web"
      ? ({
          cursor: "pointer",
          transitionProperty: "transform, background-color, box-shadow",
          transitionDuration: "120ms",
          transitionTimingFunction: "ease-out",
          userSelect: "none",
        } as unknown as ViewStyle)
      : {}),
  },
  closeActionText: {
    color: colors.grey[700],
  },
  resyncButton: {
    minHeight: 30,
    borderRadius: 6,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    justifyContent: "center",
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  resyncText: {
    color: colors.white,
  },
  successContainer: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 24,
    paddingVertical: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  successIconShell: {
    width: 48,
    height: 48,
    borderRadius: 999,
    backgroundColor: colors.success[50],
    borderWidth: 1,
    borderColor: colors.success[200],
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  successTitle: {
    color: colors.grey[900],
    marginBottom: 6,
  },
  successDescription: {
    color: colors.grey[600],
    lineHeight: 20,
    maxWidth: 300,
    textAlign: "center",
  },
  closeIconButton: {
    position: "absolute",
    right: 16,
    top: 16,
    width: 24,
    height: 24,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.7,
  },
});

export default GapAnalysisImportIssuesPage;
